import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Download, X, Share } from 'lucide-react';

const DISMISS_KEY = 'pwa-install-dismissed-until';
const DISMISS_DAYS = 7;

const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const isInStandaloneMode = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;
const isDismissed = () => {
  const until = localStorage.getItem(DISMISS_KEY);
  return until && Date.now() < Number(until);
};
const dismiss = () => {
  const until = Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000;
  localStorage.setItem(DISMISS_KEY, String(until));
};

// ── Android/Chrome banner ────────────────────────────────────
const AndroidBanner = ({ prompt, onDismiss }) => {
  const handleInstall = async () => {
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === 'accepted') onDismiss();
  };

  return (
    <BannerWrapper onDismiss={onDismiss}>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-900">Installa AgencyOS</p>
        <p className="text-xs text-slate-500">Aggiungilo alla schermata home</p>
      </div>
      <Button
        size="sm"
        className="bg-indigo-600 hover:bg-indigo-700 shrink-0 gap-1"
        onClick={handleInstall}
      >
        <Download className="h-3.5 w-3.5" />
        Installa
      </Button>
    </BannerWrapper>
  );
};

// ── iOS/Safari banner ────────────────────────────────────────
const IOSBanner = ({ onDismiss }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <BannerWrapper onDismiss={onDismiss}>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-900">Installa AgencyOS</p>
        {!expanded ? (
          <button
            className="text-xs text-indigo-600 underline underline-offset-2"
            onClick={() => setExpanded(true)}
          >
            Come installare su iPhone →
          </button>
        ) : (
          <ol className="text-xs text-slate-600 mt-1 space-y-0.5 list-decimal list-inside">
            <li>Tocca <Share className="inline h-3 w-3 mb-0.5" /> <strong>Condividi</strong> nella barra Safari</li>
            <li>Scorri e tocca <strong>"Aggiungi a schermata Home"</strong></li>
            <li>Tocca <strong>Aggiungi</strong> in alto a destra</li>
          </ol>
        )}
      </div>
    </BannerWrapper>
  );
};

// ── Wrapper comune ───────────────────────────────────────────
const BannerWrapper = ({ children, onDismiss }) => (
  <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-sm">
    <div className="bg-white border border-indigo-200 rounded-2xl shadow-xl p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center shrink-0">
        <span className="text-white font-bold text-lg">A</span>
      </div>
      {children}
      <button
        onClick={onDismiss}
        className="text-slate-400 hover:text-slate-600 shrink-0"
        aria-label="Chiudi"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  </div>
);

// ── Main component ───────────────────────────────────────────
export const InstallBanner = () => {
  const [androidPrompt, setAndroidPrompt] = useState(null);
  const [showIOS, setShowIOS] = useState(false);
  const [dismissed, setDismissed] = useState(isDismissed);

  // Android: cattura evento beforeinstallprompt
  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setAndroidPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // iOS: mostra banner se non già installato e non dismesso
  useEffect(() => {
    if (isIOS() && !isInStandaloneMode() && !isDismissed()) {
      // Aspetta 3s prima di mostrarlo
      const t = setTimeout(() => setShowIOS(true), 3000);
      return () => clearTimeout(t);
    }
  }, []);

  const handleDismiss = () => {
    dismiss();
    setDismissed(true);
    setShowIOS(false);
    setAndroidPrompt(null);
  };

  if (dismissed || isInStandaloneMode()) return null;

  if (androidPrompt) {
    return <AndroidBanner prompt={androidPrompt} onDismiss={handleDismiss} />;
  }

  if (showIOS) {
    return <IOSBanner onDismiss={handleDismiss} />;
  }

  return null;
};
